import { motion } from 'framer-motion';
import { Award, Leaf, Users, Globe } from 'lucide-react';

export const About = () => {
  return (
    <div data-testid="about-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4 font-medium">Our Story</p>
          <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground mb-6">
            About <span className="italic">Krishna Sugandh</span>
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_essence-store-24/artifacts/g312yqm0_Copilot_20260422_151639.png"
              alt="Krishna Sugandh — signature attar and crystal perfume collection"
              className="w-full h-[600px] object-cover"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col justify-center space-y-6"
          >
            <h2 className="font-display text-4xl font-light tracking-wide">
              Rooted in <span className="italic">Tradition</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground">
              Krishna Sugandh was established in 2007 in the heart of Kannauj, Uttar Pradesh – the perfume capital of India. Situated on the banks of the sacred Ganges, our city has been the epicenter of attar making for over 400 years.
            </p>
            <p className="text-lg font-light leading-relaxed text-muted-foreground">
              We are proud manufacturers, traders, and exporters of premium essential oils, traditional Indian attars, aromatic spice oils, and exquisite sandalwood products. Every product we create carries the essence of centuries-old craftsmanship passed down through generations.
            </p>
            <p className="text-lg font-light leading-relaxed text-muted-foreground">
              Our commitment to purity, quality, and authenticity has made us a trusted name among connoisseurs of fine fragrances worldwide. We use only natural, organic ingredients and traditional distillation methods to ensure that each bottle captures the true spirit of nature.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center p-8 border border-white/5 hover:border-primary/30 transition-colors duration-300"
          >
            <Award className="mx-auto text-primary mb-4" size={48} strokeWidth={1} />
            <h3 className="font-display text-3xl mb-2">18+</h3>
            <p className="text-sm uppercase tracking-widest text-muted-foreground">Years of Excellence</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center p-8 border border-white/5 hover:border-primary/30 transition-colors duration-300"
          >
            <Leaf className="mx-auto text-primary mb-4" size={48} strokeWidth={1} />
            <h3 className="font-display text-3xl mb-2">100%</h3>
            <p className="text-sm uppercase tracking-widest text-muted-foreground">Natural & Organic</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center p-8 border border-white/5 hover:border-primary/30 transition-colors duration-300"
          >
            <Users className="mx-auto text-primary mb-4" size={48} strokeWidth={1} />
            <h3 className="font-display text-3xl mb-2">10000+</h3>
            <p className="text-sm uppercase tracking-widest text-muted-foreground">Satisfied Customers</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center p-8 border border-white/5 hover:border-primary/30 transition-colors duration-300"
          >
            <Globe className="mx-auto text-primary mb-4" size={48} strokeWidth={1} />
            <h3 className="font-display text-3xl mb-2">50+</h3>
            <p className="text-sm uppercase tracking-widest text-muted-foreground">Countries Worldwide</p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-card/30 backdrop-blur-md border border-white/5 p-12 text-center"
        >
          <h2 className="font-display text-3xl md:text-4xl mb-6">
            Our <span className="italic">Mission</span>
          </h2>
          <p className="text-lg font-light leading-relaxed text-muted-foreground max-w-4xl mx-auto">
            To preserve and share the ancient art of Indian perfumery with the world, creating authentic, natural fragrances that connect people to nature and tradition. We strive to uphold the legacy of Kannauj while embracing sustainable practices and serving our global community with integrity and excellence.
          </p>
        </motion.div>
      </div>
    </div>
  );
};